import { useEffect, useState } from 'react'

const REASON_OPTIONS = ['Legal', 'Investigation', 'Formal Grievance', 'SARS']

export default function SarsRequest({ onBack }) {
  const [staffOptions, setStaffOptions] = useState([])
  const [staffLoading, setStaffLoading] = useState(false)
  const [selectedStaffEmail, setSelectedStaffEmail] = useState('')
  const [reasonType, setReasonType] = useState('Legal')
  const [reasonNotes, setReasonNotes] = useState('')
  const [sarsSubmitting, setSarsSubmitting] = useState(false)
  const [sarsMessage, setSarsMessage] = useState('')

  useEffect(() => {
    const fetchStaff = async () => {
      setStaffLoading(true)
      setSarsMessage('')
      try {
        const response = await fetch('https://api.insightos.co.uk/api/staff', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        })
        const data = await response.json()
        const options = Array.isArray(data?.data)
          ? data.data
              .filter((staff) => staff?.Email && staff?.Name)
              .map((staff) => ({
                name: staff.Name,
                email: staff.Email,
                access: staff.Access || staff.access || 'staff',
              }))
          : []

        setStaffOptions(options)
        if (options.length > 0) {
          setSelectedStaffEmail(options[0].email)
        }
      } catch (error) {
        console.error('Error fetching staff list for SARS:', error)
        setStaffOptions([])
        setSarsMessage('Failed to load staff list.')
      } finally {
        setStaffLoading(false)
      }
    }

    fetchStaff()
  }, [])

  const handleSarsSubmit = async (event) => {
    event.preventDefault()
    if (!selectedStaffEmail) return

    const selectedStaff = staffOptions.find((staff) => staff.email === selectedStaffEmail)
    if (!selectedStaff) return

    const trimmedNotes = reasonNotes.trim()
    const composedReason = trimmedNotes ? `${reasonType} - ${trimmedNotes}` : reasonType

    setSarsSubmitting(true)
    setSarsMessage('')
    try {
      const response = await fetch('https://api.insightos.co.uk/api/sarsendpoint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          Email: selectedStaff.email,
          Access: selectedStaff.access,
          Reason: composedReason,
          ReasonType: reasonType,
          ReasonDetails: trimmedNotes,
        }),
      })

      const data = await response.json()
      if (data.success) {
        setSarsMessage('SARS request submitted successfully.')
        setReasonNotes('')
        return
      }

      setSarsMessage(data.message || 'Failed to submit SARS request.')
    } catch (error) {
      console.error('Failed to submit SARS request:', error)
      setSarsMessage('Failed to submit SARS request.')
    } finally {
      setSarsSubmitting(false)
    }
  }

  return (
    <div className="flex-1 overflow-y-auto p-5">
      <div className="rounded-2xl border border-white/50 bg-white/45 backdrop-blur-md p-4">
        <h3 className="text-lg font-semibold text-blue-800">Request conversations</h3>
        <p className="mt-1 text-sm text-purple-700">Select a staff member and provide a reason.</p>

        <form onSubmit={handleSarsSubmit} className="mt-4 space-y-4">
          <div>
            <label htmlFor="sarsStaff" className="block text-sm font-medium text-blue-900">
              Staff member
            </label>
            <select
              id="sarsStaff"
              value={selectedStaffEmail}
              onChange={(event) => setSelectedStaffEmail(event.target.value)}
              className="mt-1 w-full rounded-xl border border-blue-200 bg-white/90 px-3 py-2 text-blue-800 focus:outline-none focus:ring-2 focus:ring-purple-400"
              disabled={staffLoading || sarsSubmitting || staffOptions.length === 0}
            >
              {staffOptions.map((staff) => (
                <option key={staff.email} value={staff.email}>
                  {staff.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="sarsReason" className="block text-sm font-medium text-blue-900">
              Reason
            </label>
            <select
              id="sarsReason"
              value={reasonType}
              onChange={(event) => setReasonType(event.target.value)}
              className="mt-1 w-full rounded-xl border border-blue-200 bg-white/90 px-3 py-2 text-blue-800 focus:outline-none focus:ring-2 focus:ring-purple-400"
              disabled={staffLoading || sarsSubmitting}
            >
              {REASON_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="sarsReasonNotes" className="block text-sm font-medium text-blue-900">
              {reasonType} details
            </label>
            <textarea
              id="sarsReasonNotes"
              value={reasonNotes}
              onChange={(event) => setReasonNotes(event.target.value)}
              rows={4}
              className="mt-1 w-full rounded-xl border border-blue-200 bg-white/90 px-3 py-2 text-blue-800 focus:outline-none focus:ring-2 focus:ring-purple-400"
              placeholder={`Enter ${reasonType.toLowerCase()} notes...`}
              disabled={staffLoading || sarsSubmitting}
            />
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={sarsSubmitting || staffLoading || !selectedStaffEmail}
              className="rounded-xl px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 disabled:opacity-60"
            >
              {sarsSubmitting ? 'Submitting...' : 'Submit SARS Request'}
            </button>
            <button
              type="button"
              onClick={onBack}
              className="rounded-xl px-4 py-2 text-sm font-semibold text-blue-800 bg-white/70 border border-white/60 hover:bg-white/90"
            >
              Back to chat
            </button>
          </div>

          {staffLoading && <p className="text-sm text-blue-700">Loading staff...</p>}
          {sarsMessage && <p className="text-sm text-purple-700">{sarsMessage}</p>}
        </form>
      </div>
    </div>
  )
}